import type { FormInput } from "../../ui/InputField.tsx"
import { DISPLAY_TIME_ZONE } from "../../ui/displayTime.ts"
import { localDateOf, localMidnight } from "../trading/format.ts"

// TradingView's Properties › Backtesting range (docs/design/0004): the backtest runs on the bars
// of these days only (in Indian time, as the app shows them). Kept on the pane's strategy as bar
// times (`from` inclusive, `to` exclusive), not as engine properties.

const DAY = 86_400

export const RANGE_FIELDS: ReadonlyArray<FormInput> = [
  {
    id: "rangeFrom",
    type: "date",
    title: "From date",
    tooltip: "The first day to backtest (Indian time). Empty: the first bar loaded.",
    group: "Backtesting range"
  },
  {
    id: "rangeTo",
    type: "date",
    title: "To date",
    tooltip: "The last day to backtest, included. Empty: the latest bar.",
    group: "Backtesting range"
  }
]

/** The form's dates ("" when open) from the pane's strategy. */
export const rangeValues = (range: { readonly from?: number | undefined; readonly to?: number | undefined }) => ({
  rangeFrom: range.from === undefined ? "" : localDateOf(range.from, DISPLAY_TIME_ZONE),
  rangeTo: range.to === undefined ? "" : localDateOf(range.to - 1, DISPLAY_TIME_ZONE)
})

/** The bar times a form's dates mean: from the first day's midnight to the midnight after the last day. */
export const rangeOf = (values: Readonly<Record<string, unknown>>): { readonly from: number | null; readonly to: number | null } => {
  const from = localMidnight(String(values["rangeFrom"] ?? ""), DISPLAY_TIME_ZONE)
  const last = localMidnight(String(values["rangeTo"] ?? ""), DISPLAY_TIME_ZONE)
  // The day after the last one, found from its noon so a daylight-saving change can't skip it.
  const to = last === null ? null : localMidnight(localDateOf(last + DAY + DAY / 2, DISPLAY_TIME_ZONE), DISPLAY_TIME_ZONE)
  return { from, to }
}

/** "To date" before "From date": what the form says instead of saving it. */
export const rangeProblem = (values: Readonly<Record<string, unknown>>): string | null => {
  const { from, to } = rangeOf(values)
  return from !== null && to !== null && to <= from ? "The To date is before the From date." : null
}

/** The bars the range keeps. */
export const barsInRange = <B extends { readonly time: number }>(
  bars: ReadonlyArray<B>,
  range: { readonly from?: number | undefined; readonly to?: number | undefined }
): ReadonlyArray<B> =>
  range.from === undefined && range.to === undefined
    ? bars
    : bars.filter((bar) => (range.from === undefined || bar.time >= range.from) && (range.to === undefined || bar.time < range.to))
